/* eslint-disable react/jsx-wrap-multilines */
import React, { useCallback, useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import { COMPANIES_NAV_PAGE_LIST, TABLE_COLUMNS } from './constants';
import saga from './saga';
import reducer from './reducer';
import { useInjectSaga } from '../../../utils/injectSaga';
import { useInjectReducer } from '../../../utils/injectReducer';
import { getDictionaryDataSelector, getIsDictionaryDataLoadingSelector, getPaginationSelector } from './selectors';
import { getDictionaryDataAction, resetStateAction } from './actions';
import { PageContentWithNav } from '../../../components/page-content-with-nav';
import { removeModalQuery } from '../../../utils/removeModalQuery';
import { normalizeDataGridModel } from '../../../utils/normalizeDataGridModel';
import { DataGridComponent } from '../../../components/data-grid';

const CompaniesDictionaryWrapper = ({
	onGetDictionaryData,
	dictionaryData,
	isDictionaryDataLoading,
	pagination,
	onResetState,
}) => {
	useInjectSaga({ key: 'companiesDictionarySaga', saga });
	useInjectReducer({ key: 'companiesDictionaryReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		onGetDictionaryData({ searchQuery: removeModalQuery(query.toString()) });

		return () => onResetState();
	}, []);

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);
		history.push(`${pathname}?${query}`);
		onGetDictionaryData({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);
		if (value === 'all') {
			query.delete('per_page');
			history.push(`${pathname}?${query}`);
		} else {
			query.set('per_page', value);
			history.push(`${pathname}?${query}`);
		}
		onGetDictionaryData({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		history.push(`${pathname}?${query}`);
		onGetDictionaryData({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_COLUMNS()));

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);
			onGetDictionaryData({ searchQuery: removeModalQuery(query.toString()) });
		}
	});

	return (
		<PageContentWithNav
			pageNavigationItems={COMPANIES_NAV_PAGE_LIST}
			pageContent={
				<DataGridComponent
					headerText="Компании"
					columns={TABLE_COLUMNS()}
					rows={dictionaryData}
					pagination={
						!pagination
							? null
							: { ...pagination, onPageChange: handlePageChange, onSelectChange: handleSelectChange }
					}
					isTableDataLoading={isDictionaryDataLoading}
					handleSortChange={handleSortChange}
					handleFilterChange={handleFilterChange}
				/>
			}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	dictionaryData: getDictionaryDataSelector(),
	isDictionaryDataLoading: getIsDictionaryDataLoadingSelector(),
	pagination: getPaginationSelector(),
});

const mapDispatchToProps = {
	onGetDictionaryData: getDictionaryDataAction,
	onResetState: resetStateAction,
};

const CompaniesDictionaryContainer = connect(mapStateToProps, mapDispatchToProps)(CompaniesDictionaryWrapper);

CompaniesDictionaryWrapper.propTypes = {
	onGetDictionaryData: PropTypes.func.isRequired,
	dictionaryData: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			name: PropTypes.string.isRequired,
			inn: PropTypes.string.isRequired,
			ogrn: PropTypes.string.isRequired,
			contact: PropTypes.objectOf(PropTypes.string).isRequired,
			link: PropTypes.string.isRequired,
		}),
	),
	isDictionaryDataLoading: PropTypes.bool.isRequired,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	onResetState: PropTypes.func.isRequired,
};

export default CompaniesDictionaryContainer;
